import { createContext, useEffect, useState, type ReactNode } from 'react';

import {
  defaultTheme,
  themeClass,
  themeStorageKey,
} from '@constants/theme.constant';
import { ETheme } from '@enums/theme.enum';

export interface ThemeContextType {
  actualTheme: 'dark' | 'light';
  setTheme: (theme: ETheme) => void;
  theme: ETheme;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined,
);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<ETheme>(() => {
    const stored = localStorage.getItem(themeStorageKey) as ETheme | null;
    if (stored && Object.values(ETheme).includes(stored)) {
      return stored;
    }
    return defaultTheme;
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  const isDark =
    theme === ETheme.Dark || (theme === ETheme.System && systemPrefersDark);
  const actualTheme = isDark ? 'dark' : 'light';

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    if (theme === ETheme.System) {
      mediaQuery.addEventListener('change', handleChange);
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, [theme]);

  useEffect(() => {
    if (actualTheme === 'dark') {
      document.documentElement.classList.add(themeClass);
    } else {
      document.documentElement.classList.remove(themeClass);
    }
  }, [actualTheme]);

  useEffect(() => {
    localStorage.setItem(themeStorageKey, theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ actualTheme, setTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
}
